import { Component, OnInit, ViewEncapsulation, effect, input, model, output, inject } from '@angular/core';
import { FormControl, NgControl, ValidatorFn } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { ApContentService, ApContentComponent, ApContentPipe } from '@ap/content';
import { ErrorMessages, ApFormErrorsComponent } from '@ap/form/errors';
import { ApNotificationComponent } from '@ap/notification';
import { ApTableModule } from '@ap/table';
import { ApProgressComponent } from '@ap/progress';
import { ApActionIconComponent, ApIconComponent } from '@ap/icon';

import { ApUploadButtonComponent } from '../upload-button/upload-button.component';
import { ApDropzoneComponent } from '../dropzone/dropzone.component';
import { ReadFile, UploadStatus } from '../read-file.model';
import { FILE_VALIDATORS, FilesValidator } from '../file.model';
import { ApBytesPipe } from '../byte/bytes.pipe';
import { ApNamePipe } from '../name/name.pipe';

import content from './selector.content.json';
import { DefaultFileExtensions, FileTableColumnNames } from './selector.model';

@Component({
    selector: 'ap-file-selector',
    templateUrl: './selector.component.html',
    styleUrls: ['./selector.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ApDropzoneComponent,
        ApUploadButtonComponent,
        ApContentComponent,
        ApFormErrorsComponent,
        ApNotificationComponent,
        ApTableModule,
        ApProgressComponent,
        ApActionIconComponent,
        ApIconComponent,
        ApContentPipe,
        ApBytesPipe,
        ApNamePipe
    ]
})
export class ApFileSelectorComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly fileValidators = inject(FILE_VALIDATORS, { optional: true });
    protected control = inject(NgControl, { self: true, optional: true });

    /**
     * Required - The FormControl to be used for the input field
     */
    public readonly selectorFormControl = input.required<FormControl<ReadFile[]>>();

    /**
     * An array of selectedFiles
     */
    public readonly selectedFiles = input<ReadFile[]>(undefined);

    /**
     * Set to true to disable the field
     */
    public readonly isDisabled = input(false);

    /**
     * An array of acceptedFileExtensions
     */
    public readonly acceptedFileExtensions = model<string[]>();

    /**
     * Maximum file size; default = 10
     */
    public readonly fileSizeLimitInMB = input(10);

    /**
     * Is it a compact file selector? Default is false
     */
    public readonly compact = input(false);

    /**
     * Maximum amount of files allowed; 50 if not set
     */
    public readonly maxFilesAllowed = input(50);

    /**
     * Whether to show the header, default is false
     */
    public readonly showHeader = input(false);

    /**
     * Whether to hide the upload field, default is false
     */
    public readonly hideUploadField = input(false);

    /**
     * Whether to show a file preview, only works when maxFilesAllowed = 1. Default is false
     */
    public readonly uploadPreview = input(false);

    /**
     * Emits a list of all files when a file is selected
     */
    public readonly filesSelected = output<ReadFile[]>();

    /**
     * Emits the file that has been deleted
     */
    public readonly fileDeleted = output<ReadFile>();

    /** @ignore */
    public displayedColumns: FileTableColumnNames[];

    /** @ignore */
    public uploadStatus = UploadStatus;

    /** @ignore */
    public multipleFilesSelected = false;

    /** @ignore */
    public validators: FilesValidator[] = [];

    /** @ignore */
    private _files: ReadFile[];

    /**
     * Json object that contains default content messages from form element
     */
    protected readonly content = model<{
        [key: string]: any;
    }>(undefined);

    constructor() {
        this.contentService.addContentItems(content);
        this.content.set(content);

        effect(() => {
            if (this.selectedFiles()?.length > 0 && this.files !== this.selectedFiles()) {
                this.files = this.selectedFiles();
            }
        });
    }

    protected documentTypeMessages: ErrorMessages;
    protected selectorMessages: ErrorMessages;

    /** @ignore */
    public get allowMultiple(): boolean {
        return this.maxFilesAllowed() !== 1;
    }

    /** @ignore */
    public get reachedMaximumFileAmount(): boolean {
        const maxFilesAllowed = this.maxFilesAllowed();
        return maxFilesAllowed && this.files.length >= maxFilesAllowed;
    }

    /** @ignore */
    public get additionalTextKey() {
        return this.allowMultiple ? 'file.selector.additionalText.multiple' : 'file.selector.additionalText';
    }

    public get files(): ReadFile[] {
        return this._files;
    }

    public set files(value: ReadFile[]) {
        this._files = value;
        this.selectorFormControl()?.setValue(value);
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.setErrorMessages();
        this.files = this.files || [];
        this.displayedColumns = [FileTableColumnNames.NAME, FileTableColumnNames.SIZE, FileTableColumnNames.REMOVE];
        this.addUploadingValidatorFn();

        const acceptedFileExtensions = this.acceptedFileExtensions();
        if (!Array.isArray(acceptedFileExtensions) || acceptedFileExtensions.length === 0) {
            this.acceptedFileExtensions.set(DefaultFileExtensions);
        }

        if (this.fileValidators) {
            this.registerValidator(...(this.fileValidators as unknown as FilesValidator[]));
        }
    }

    /** @ignore */
    public resetSelector() {
        this.runValidators([], []);
        this.files = [];
    }

    /** @ignore */
    public deselectFile(file: ReadFile): void {
        const index = this.files.findIndex((item) => file.name === item.name && file.size === item.size);
        const currentFiles = this.files.slice();
        const deletedFile: ReadFile = this.files.splice(index, 1)[0];
        // need to reset this.files because splicing doesn't register as a change (to update table)
        this.files = [].concat(this.files);
        this.runValidators(currentFiles, this.files);

        this.fileDeleted.emit(deletedFile);
        this.emitChanges();
    }

    /** @ignore */
    public runValidators(previousValue: ReadFile[], currentValue: ReadFile[]): boolean {
        const allValid = this.validators.map((validator) => validator.validate(previousValue, currentValue));
        return allValid.every((value) => !!value);
    }

    /** @ignore */
    public addFiles(fileList: ReadFile[]): void {
        this.multipleFilesSelected = fileList.length > 1;

        if (!this.runValidators(this.files, [...this.files, ...fileList])) {
            this.files = this.files.concat([]);
            return;
        }

        fileList.forEach((file) => this.addFile(file));
        this.emitChanges();
    }

    /** @ignore */
    public getFileExtensionsString(): string {
        return this.acceptedFileExtensions()
            .map((extension) => `.${extension}`)
            .join(', ');
    }

    /** @ignore */
    public showUploadField(): boolean {
        return !this.hideUploadField() && (this.allowMultiple || this.files.length === 0);
    }

    /** @ignore */
    public addUploadingValidatorFn(): void {
        const selectorFormControl = this.selectorFormControl();
        if (!selectorFormControl) {
            return;
        }
        const validatorArray: ValidatorFn[] = [
            () =>
                this.files.filter((file) => file.status === this.uploadStatus.UPLOADING || file.status === this.uploadStatus.PROCESSING)
                    .length > 0
                    ? { uploading: true }
                    : null
        ];
        if (selectorFormControl.validator) {
            validatorArray.push(selectorFormControl.validator);
        }
        selectorFormControl.setValidators(validatorArray);
    }

    /** @ignore */
    public hasFailedFiles(): boolean {
        return this.files.filter((file) => file.status === this.uploadStatus.FAILED).length > 0;
    }

    /** @ignore */
    public getHeaderColumns(): FileTableColumnNames[] {
        return this.showHeader() ? this.displayedColumns : [];
    }

    /** @ignore */
    public showFileSize(status: UploadStatus): boolean {
        return status !== UploadStatus.UPLOADING;
    }

    /** @ignore */
    public showRemoveButton(status: UploadStatus) {
        return status === UploadStatus.NEW || status === UploadStatus.FAILED || status === UploadStatus.DONE;
    }

    /** @ignore */
    public registerValidator(...validators: FilesValidator[]): void {
        validators.forEach((validator) => {
            validator.fileSelector = this;
            this.validators.push(validator);
        });
    }

    public isImage(readFile: ReadFile): boolean {
        return /(^image)(\/)[a-zA-Z0-9_]*/.test(readFile.type);
    }
    public isPDF(readFile: ReadFile): boolean {
        return /(^pdf)(\/)[a-zA-Z0-9_]*/.test(readFile.type);
    }

    public getPreview(readFile: ReadFile): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(readFile));
    }

    private setErrorMessages(): void {
        this.documentTypeMessages = {
            required: this.contentService.get('file.selector.documenttype.error.required')
        };
        this.selectorMessages = {
            uploading: this.contentService.get('file.selector.error.uploading'),
            failed: this.contentService.get('file.selector.error.failed'),
            required: this.contentService.get('file.selector.error.required')
        };
    }

    private addFile(file: ReadFile): void {
        file.status = UploadStatus.NEW;
        // files.push doesn't register as a change (to update table), so we concat

        this.files = this.files.concat([file]);
    }

    private emitChanges(): void {
        this.filesSelected.emit(this.files);
    }
}
